import { expected } from '@aloft/toolkit-shared';

import { usePublicRecordsForOrder } from '../../../controller/hooks/usePublicRecordsForOrder.ts';
import { useTkv2NullableOrderContext } from '../../../controller/hooks/useTkv2NullableControllerOrder.ts';
import { useTkv2PairedCostSummaryDataV2 } from '../../../controller/routes/useTkv2PairedCostSummaryDataV2.tsx';
import { selectUser } from '../../../redux/features/selectors/accountSelectors.ts';
import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectSiteCostPerSFs } from '../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { selectSiteSizes } from '../../../redux/features/selectors/selectSiteSize.ts';
import { selectSiteValues } from '../../../redux/features/selectors/selectSiteValue.ts';
import { selectSiteValueRatios } from '../../../redux/features/selectors/selectSiteValueRatio.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { getSiteSizeVsPriceChartData } from '../../summaryV2/AdjustmentBreakdownV2/SiteValueCharts/getSiteSizeVsPriceChartData.ts';
import { getSiteSizeVsSiteValueChartData } from '../../summaryV2/AdjustmentBreakdownV2/SiteValueCharts/getSiteSizeVsSiteValueChartData.ts';
import { getSiteToValueRatioChartData } from '../../summaryV2/AdjustmentBreakdownV2/SiteValueCharts/getSiteToValueRatioChartData.ts';

export type Tkv2SiteValueChartsParams = {
  siteSizeVsSiteValueChartData: {
    siteName: string;
    siteSize: number | undefined;
    siteValue: number | undefined;
  }[];
  siteSizeVsPriceChartData: {
    siteName: string;
    siteSize: number | undefined;
    siteCostPerSF: number | undefined;
  }[];
  siteToValueRatioChartData:
    | {
        marketLandToValueRatio?: number | undefined;
        siteToValueRatio: number | undefined;
        siteSize: number | undefined;
        siteName: string;
      }[]
    | undefined;
  publicRecordsIsLoading: boolean | undefined;
};

export const useTkv2SiteValueChartsParams = (): Tkv2SiteValueChartsParams => {
  const ctx = useTkv2NullableOrderContext();

  const publicRecords = usePublicRecordsForOrder();

  const compSequence = useAppSelector(selectCompSequence);

  const siteSizes = useAppSelector(selectSiteSizes);
  const siteValues = useAppSelector(selectSiteValues);
  const siteCostPerSFs = useAppSelector(selectSiteCostPerSFs);
  const siteToValueRatios = useAppSelector(selectSiteValueRatios);

  const { id: accountID } = useAppSelector(selectUser);
  const summaryData = useTkv2PairedCostSummaryDataV2(ctx, expected(accountID));

  const siteSizeVsSiteValueChartData = getSiteSizeVsSiteValueChartData(
    summaryData,
    compSequence,
    siteSizes,
    siteValues,
  );

  const siteSizeVsPriceChartData = getSiteSizeVsPriceChartData(
    summaryData,
    compSequence,
    siteSizes,
    siteCostPerSFs,
  );

  const siteToValueRatioChartData = getSiteToValueRatioChartData(
    summaryData,
    publicRecords,
    compSequence,
    siteSizes,
    siteToValueRatios,
  );
  const publicRecordsIsLoading = publicRecords.isLoading;

  return {
    siteSizeVsSiteValueChartData,
    siteSizeVsPriceChartData,
    siteToValueRatioChartData,
    publicRecordsIsLoading,
  };
};
